import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpenIcon, 
  AcademicCapIcon, 
  UserGroupIcon, 
  ChartBarIcon, 
  ClockIcon, 
  CalendarIcon, 
  CheckCircleIcon,
  BeakerIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  TrophyIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  BookmarkIcon,
  DocumentTextIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  MicrophoneIcon,
  HeartIcon,
  PuzzlePieceIcon,
  UserIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const Academics = () => {
  // Foundational Program (Preschool - 3 to 6 years)
  const foundationalProgram = {
    title: 'Foundational Program',
    age: '3 - 6 years',
    description: 'Preschool program focusing on holistic development through practical life skills, sensory learning, language development, and creative expression.',
    categories: [
      {
        title: 'Practical Life',
        icon: HeartIcon,
        color: 'from-pink-500 to-rose-500',
        description: 'Physical environment, care of self, coordination, control of movement, exploration tasks, self-discipline, concentration, and self-confidence.',
        skills: ['Physical Environment', 'Coordination', 'Control of Movement', 'Exploration', 'Self-Discipline', 'Concentration', 'Self-Confidence']
      },
      {
        title: 'Sensorial',
        icon: PuzzlePieceIcon,
        color: 'from-yellow-500 to-orange-500',
        description: 'Intellectual and intellectual development through sensory materials, specific qualities, sense of observation, and sense of administration.',
        skills: ['Sensory Materials', 'Intellectual Development', 'Senses Exploration', 'Observation Skills']
      },
      {
        title: 'Language',
        icon: MicrophoneIcon,
        color: 'from-blue-500 to-cyan-500',
        description: 'Language acquisition through hearing, touching, reading, and writing activities, with a focus on precision and expression.',
        skills: ['Listening', 'Speaking', 'Reading', 'Writing', 'Vocabulary Building']
      },
      {
        title: 'Mathematics',
        icon: ChartBarIcon,
        color: 'from-green-500 to-emerald-500',
        description: 'Introduction to mathematical concepts through hands-on activities, geometric understanding, and number sense development.',
        skills: ['Number Sense', 'Geometric Concepts', 'Counting', 'Patterns', 'Measurements']
      },
      {
        title: 'Craft & Culture',
        icon: PaintBrushIcon,
        color: 'from-purple-500 to-indigo-500',
        description: 'Skilled hand creation, decoration objects, manual skills, material understanding, cultural traditions, painting, and art exploration.',
        skills: ['Hand Skills', 'Decoration', 'Manual Skills', 'Cultural Understanding', 'Painting', 'Art']
      }
    ]
  };

  // Preparatory Program (Class 1-2, 6-8 years)
  const preparatoryProgram = {
    title: 'Preparatory Program',
    age: '6 - 8 years',
    classes: 'Class 1 - 2',
    description: 'Building strong foundation in languages, mathematics, and environmental science with co-curricular activities.',
    subjects: [
      { name: 'First Language', detail: 'English', color: 'bg-blue-100 text-blue-700' },
      { name: 'Second Language', detail: 'Kannada', color: 'bg-green-100 text-green-700' },
      { name: 'Third Language', detail: 'Hindi', color: 'bg-orange-100 text-orange-700' },
      { name: 'Mathematics', detail: 'Core Mathematics', color: 'bg-purple-100 text-purple-700' },
      { name: 'Environmental Science', detail: 'EVS', color: 'bg-teal-100 text-teal-700' },
      { name: 'Co-Curricular', detail: 'Music, Dance, Sports, Arts', color: 'bg-pink-100 text-pink-700' }
    ],
    coCurricular: ['Music', 'Dance', 'Sports', 'Arts']
  };

  // Preparatory Program (Class 3-5, 8-11 years)
  const preparatoryProgram2 = {
    title: 'Preparatory Program (Upper)',
    age: '8 - 11 years',
    classes: 'Class 3 - 5',
    description: 'Comprehensive curriculum with core subjects, languages, and co-curricular activities for holistic development.',
    subjects: [
      { name: 'First Language', detail: 'English', color: 'bg-blue-100 text-blue-700' },
      { name: 'Second Language', detail: 'Kannada', color: 'bg-green-100 text-green-700' },
      { name: 'Third Language', detail: 'Hindi', color: 'bg-orange-100 text-orange-700' },
      { name: 'Mathematics', detail: 'Core Mathematics', color: 'bg-purple-100 text-purple-700' },
      { name: 'Science', detail: 'General Science', color: 'bg-cyan-100 text-cyan-700' },
      { name: 'Social Studies', detail: 'History, Geography, Civics', color: 'bg-yellow-100 text-yellow-700' },
      { name: 'Arts', detail: 'Music, Dance, Visual Arts', color: 'bg-pink-100 text-pink-700' },
      { name: 'Sports', detail: 'Cricket, Volleyball, Chess, Carrom, Badminton, Yoga', color: 'bg-red-100 text-red-700' }
    ]
  };

  // Middle School Program (Class 6-8, 11-14 years)
  const middleSchoolProgram = {
    title: 'Middle School Program',
    age: '11 - 14 years',
    classes: 'Class 6 - 8',
    description: 'Advanced curriculum with core subjects, languages, and extensive co-curricular activities.',
    subjects: [
      { name: 'First Language', detail: 'English', color: 'bg-blue-100 text-blue-700' },
      { name: 'Second Language', detail: 'Kannada', color: 'bg-green-100 text-green-700' },
      { name: 'Third Language', detail: 'Hindi', color: 'bg-orange-100 text-orange-700' },
      { name: 'Mathematics', detail: 'Core Mathematics', color: 'bg-purple-100 text-purple-700' },
      { name: 'Science', detail: 'Physics, Chemistry, Biology', color: 'bg-cyan-100 text-cyan-700' },
      { name: 'Social Science', detail: 'History, Geography, Civics', color: 'bg-yellow-100 text-yellow-700' },
      { name: 'Co-Curricular', detail: 'Dance, Music, Art', color: 'bg-pink-100 text-pink-700' },
      { name: 'Sports', detail: 'Cricket, Volleyball, Football, Kabaddi, Kho-Kho, Badminton, Yoga', color: 'bg-red-100 text-red-700' }
    ]
  };

  // Secondary School Program (Class 9-10, 14-16 years)
  const secondaryProgram = {
    title: 'Secondary School Program',
    age: '14 - 16 years',
    classes: 'Class 9 - 10',
    description: 'Board preparation program with comprehensive curriculum and career guidance.',
    subjects: [
      { name: 'First Language', detail: 'English', color: 'bg-blue-100 text-blue-700' },
      { name: 'Second Language', detail: 'Kannada', color: 'bg-green-100 text-green-700' },
      { name: 'Third Language', detail: 'Hindi', color: 'bg-orange-100 text-orange-700' },
      { name: 'Mathematics', detail: 'Core Mathematics', color: 'bg-purple-100 text-purple-700' },
      { name: 'Science', detail: 'Physics, Chemistry, Biology', color: 'bg-cyan-100 text-cyan-700' },
      { name: 'Social Science', detail: 'History, Geography, Civics, Economics', color: 'bg-yellow-100 text-yellow-700' },
      { name: 'Co-Curricular', detail: 'Dance, Music, Art', color: 'bg-pink-100 text-pink-700' },
      { name: 'Sports', detail: 'Cricket, Volleyball, Football, Kabaddi, Kho-Kho, Badminton, Yoga', color: 'bg-red-100 text-red-700' }
    ]
  };

  const highlights = [
    { icon: BeakerIcon, value: '5', label: 'Science Labs', color: 'from-blue-500 to-blue-600' },
    { icon: ComputerDesktopIcon, value: '3', label: 'Computer Labs', color: 'from-green-500 to-green-600' },
    { icon: BookOpenIcon, value: '15,000+', label: 'Library Books', color: 'from-purple-500 to-purple-600' },
    { icon: UserGroupIcon, value: '30:1', label: 'Student-Teacher Ratio', color: 'from-orange-500 to-orange-600' }
  ];

  const features = [
    { icon: ChartBarIcon, title: 'Smart Classrooms', description: 'Digital learning with smart boards and interactive content', color: 'from-blue-500 to-blue-600' },
    { icon: ClockIcon, title: 'Flexible Timings', description: 'Well-planned schedule for balanced academic and extracurricular activities', color: 'from-green-500 to-green-600' },
    { icon: CalendarIcon, title: 'Annual Calendar', description: 'Organized academic milestones and clear event schedule', color: 'from-purple-500 to-purple-600' },
    { icon: CheckCircleIcon, title: 'Regular Assessments', description: 'Continuous evaluation through tests, projects, and activities', color: 'from-orange-500 to-orange-600' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-lg rounded-full px-6 py-2 mb-4">
            <span className="text-sm font-medium">📚 Academic Excellence</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Academics</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Comprehensive curriculum from Foundational to Secondary level, designed for holistic development and academic excellence.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {highlights.map((item, index) => (
              <div key={index} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 text-center hover:shadow-lg transition">
                <div className={`inline-block p-3 rounded-xl bg-gradient-to-r ${item.color} mb-3`}>
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-800">{item.value}</p>
                <p className="text-sm text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Foundational Program */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sm font-bold text-pink-600 uppercase tracking-wider bg-pink-50 px-4 py-1 rounded-full">Early Years</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              {foundationalProgram.title} <span className="text-pink-600">({foundationalProgram.age})</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-rose-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">{foundationalProgram.description}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {foundationalProgram.categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className={`bg-gradient-to-r ${category.color} p-4 text-white`}>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold">{category.title}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {category.skills.map((skill, idx) => (
                        <span key={idx} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Preparatory Program (Class 1-2) */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-4 py-1 rounded-full">Primary</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              {preparatoryProgram.title} <span className="text-blue-600">({preparatoryProgram.age})</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">{preparatoryProgram.description}</p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {preparatoryProgram.subjects.map((subject, index) => (
                <div key={index} className={`${subject.color} rounded-xl p-4 text-center`}>
                  <p className="text-xs font-semibold uppercase opacity-75">{subject.name}</p>
                  <p className="font-bold text-base">{subject.detail}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <span className="inline-block px-6 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold">
                🎵 Co-Curricular: Music, Dance, Sports, Arts
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Preparatory Program Upper (Class 3-5) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-wider bg-indigo-50 px-4 py-1 rounded-full">Primary Upper</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              {preparatoryProgram2.title} <span className="text-indigo-600">({preparatoryProgram2.age})</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-blue-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">{preparatoryProgram2.description}</p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {preparatoryProgram2.subjects.map((subject, index) => (
                <div key={index} className={`${subject.color} rounded-xl p-4 text-center`}>
                  <p className="text-xs font-semibold uppercase opacity-75">{subject.name}</p>
                  <p className="font-bold text-sm">{subject.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Middle School Program */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sm font-bold text-green-600 uppercase tracking-wider bg-green-50 px-4 py-1 rounded-full">Middle School</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              {middleSchoolProgram.title} <span className="text-green-600">({middleSchoolProgram.age})</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">{middleSchoolProgram.description}</p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {middleSchoolProgram.subjects.map((subject, index) => (
                <div key={index} className={`${subject.color} rounded-xl p-4 text-center`}>
                  <p className="text-xs font-semibold uppercase opacity-75">{subject.name}</p>
                  <p className="font-bold text-sm">{subject.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Secondary School Program */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sm font-bold text-purple-600 uppercase tracking-wider bg-purple-50 px-4 py-1 rounded-full">Secondary</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              {secondaryProgram.title} <span className="text-purple-600">({secondaryProgram.age})</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full mt-2"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">{secondaryProgram.description}</p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {secondaryProgram.subjects.map((subject, index) => (
                <div key={index} className={`${subject.color} rounded-xl p-4 text-center`}>
                  <p className="text-xs font-semibold uppercase opacity-75">{subject.name}</p>
                  <p className="font-bold text-sm">{subject.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-4 py-1 rounded-full">Academic Features</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-3">
              Academic <span className="text-blue-600">Highlights</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full mt-2"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center">
                  <div className={`inline-block p-4 rounded-2xl bg-gradient-to-r ${feature.color} mb-4`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <RocketLaunchIcon className="h-16 w-16 mx-auto mb-4 text-yellow-300" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Begin Your <span className="text-yellow-300">Academic Journey</span>?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Join Vidya Bharati and experience quality education that prepares you for life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/admissions" 
              className="bg-yellow-400 text-gray-900 px-8 py-3 rounded-lg font-bold hover:bg-yellow-300 transition hover:shadow-2xl inline-block"
            >
              Apply for Admission
            </Link>
            <Link 
              to="/contact" 
              className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition inline-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Academics;
